<template>
    <div v-if="if_single_tab">
        <button 
            class="btn btn-white" 
            type="button"
            id="export-button"
            @click="exportCharts('section')"
        >
            <img 
                v-if="!loading"
                class="icon pe-0 mb-0" 
                src="/img/icons/avid-icons/presention-chart.svg" 
                alt="Presentation Icon"
            >
            <div 
                v-else
                class="spinner-border spinner-border-sm float-left me-0" 
                role="status"
            ></div>
            <span class="ps-1" v-if="!is_filter_panel_expanded && !if_mobile">Generate Slides</span>
        </button>
    </div>
    <div v-else class="dropdown">
        <button 
            class="btn btn-white dropdown-toggle" 
            type="button" 
            data-bs-toggle="dropdown" 
            aria-expanded="false"
            id="export-button"
        >
            <img 
                v-if="!loading"
                class="icon pe-0 mb-0" 
                src="/img/icons/avid-icons/presention-chart.svg" 
                alt="Presentation Icon"
            >
            <div 
                v-else
                class="spinner-border spinner-border-sm float-left me-0" 
                role="status"
            ></div>
            <span class="ps-1" v-if="!is_filter_panel_expanded && !if_mobile">Generate Slides</span>
        </button>
        <ul class="dropdown-menu dropdown-menu-primary" :class="if_mobile ? 'mobile-export-dropdown' : 'dropdown-menu-end'" :data-bs-popper="if_mobile ? 'none' : null">
            <li>
                <a 
                    class="dropdown-item px-3 py-2" 
                    href="#" 
                    @click.prevent="exportCharts('section')"
                >
                    Export This Tab
                </a>
            </li>
            <li>
                <a 
                    class="dropdown-item px-3 py-2" 
                    href="#"  
                    @click.prevent="exportCharts('all')"
                >
                    Export All Tabs
                </a>
            </li>
        </ul>
    </div>
</template>

<script>
import { defineComponent } from 'vue';
import chartExportMixin from '../../mixins/chart_export';
import { Context } from "svgcanvas";

export default defineComponent({
    name: 'ExportChartsButton',
    props: {
        page: {
            type: Object,
            required: true
        },
        client: {
            type: Object,
            required: true
        },
        user: {
            type: Object,
            default: null
        },
        is_filter_panel_expanded: {
            type: Boolean,
            default: false
        },
        if_single_tab: {
            type: Boolean,
            default: false
        }
    },
    mixins: [chartExportMixin],
    data() {
        return {
            sizes: {
                FULL_WIDTH: { width: 1200, height: 450 },
                SINGLE_SMALL: { width: 750, height: 450 },
                THREE_CHARTS: { width: 400, height: 400 },
                TWO_CHARTS: { width: 600, height: 400 }
            },
            scope: 'all',
            loading: false,
            if_mobile: window.innerWidth < 768
        };
    },
    mounted() {
        this.onResize = () => {
            this.if_mobile = window.innerWidth < 768;
        };
        window.addEventListener('resize', this.onResize);
        // Ensure flag correct on mount
        this.onResize();
    },
    unmounted() {
        window.removeEventListener('resize', this.onResize);
    },
    methods: {
        exportCharts(scope) {
            this.scope = scope;
            this.loading = true;
            window.axios.get('/api/export/sizes/' + this.client.id)
                .then(response => {
                    this.sizes = response.data.sizes;
                    this.triggerDownload();
                });
        },
        async loopThroughSections() {
            const sections = this.page.sections;
            if (!sections || sections.length === 0) return [];

            // Initialize array to store all captured SVGs
            let allSVGs = [];
            let startIndex = sections.findIndex(sec => sec.id === this.currentSectionId);
            
            if (startIndex < 0) {
                startIndex = 0;
                this.currentSectionId = sections[0].id;
            }

            let currentIndex = startIndex;
            const totalSections = sections.length;

            do {
                    // Emit event to parent to set active tab at currentIndex
                this.$emit('tab', currentIndex);

                    // Wait for the DOM to update after tab change
                await this.$nextTick();
                    //await new Promise(resolve => setTimeout(resolve, 100));

                    // Capture the SVGs for this section
                this.processCharts();
                    let svgs = this.getSVGs();
                    
                    // Add captured SVGs to the combined array
                allSVGs.push(...svgs);

                currentIndex = (currentIndex + 1) % totalSections;
            } while (currentIndex !== startIndex);

            // After looping, return to the original section
            this.$emit('tab', startIndex);
            await this.$nextTick();

            // Return all combined SVGs
            return allSVGs;
        },
        triggerDownload() {
            this.currentSectionId = window.location.pathname.split('/').slice(-1);
            //If it is an array, just get the first element
            if(Array.isArray(this.currentSectionId)){
                this.currentSectionId = this.currentSectionId[0];
            }
            //If it is not a number
            if(isNaN(this.currentSectionId)){
                this.currentSectionId = this.page.sections[0].id;
            } else {
                //Cast a string to a number just in case
                this.currentSectionId = parseInt(this.currentSectionId);
            }

            if (this.scope === 'section') {
                this.processCharts();
                const svgs = this.getSVGs();
                this.requestPPT(svgs, this.currentSectionId);
            } else {
                //I need to loop through all of the different sections (via the tabs)
                this.loopThroughSections().then(combinedSVGs => {
                    this.requestPPT(combinedSVGs, null);
                });
            }
        },
        requestPPT(svgs, section_id) {
            const data = {
                client: this.client,
                page: this.page,
                params: document.location.search !== '' ? document.location.search.substring(1) : '',
                section_id: section_id,
                svgs: svgs
            };

            window.axios.post('/api/export/page', data, { responseType: 'blob' })
                .then(response => {
                        // Create a URL for the blob and trigger a download
                    const url = window.URL.createObjectURL(new Blob([response.data]));
                    const link = document.createElement('a');
                    link.href = url;

                    let name = `${this.page.name} - ${this.getDate()}.pptx`;
                    if (this.scope === 'section') {
                        name = `${this.page.name} - Section #${section_id} - ${this.getDate()}.pptx`;
                    }

                    link.setAttribute('download', name);
                    document.body.appendChild(link);
                    link.click();
                    link.remove();
                    this.loading = false;
                })
                .catch(error => {
                    console.error(error);
                });
        },
        processCharts() {
            //Resize the sections
            const containers = document.querySelectorAll('.size-container');
            
            for (let i = 0; i < containers.length; i++) {
                const container = containers[i];
                const chart = container.querySelector("canvas");
                if (!chart) continue; //If it is a table or html

                //Get the size
                const size = container.dataset.size;
                //Full Width
                if (size === 'x-large' || size === 'large') {
                    chart.dataset.width = this.sizes.FULL_WIDTH.width;
                    chart.dataset.height = this.sizes.FULL_WIDTH.height;
                } else if (size === 'small' && containers[i + 1] && containers[i + 2] && 
                          containers[i + 1].dataset.size === 'small' && 
                          containers[i + 2].dataset.size === 'small') {
                    //If this chart and the next two are all small...
                    chart.dataset.width = this.sizes.THREE_CHARTS.width;
                    chart.dataset.height = this.sizes.THREE_CHARTS.height;
                    //And set the next two dataset values
                    containers[i + 1].querySelector("canvas").dataset.width = this.sizes.THREE_CHARTS.width;
                    containers[i + 1].querySelector("canvas").dataset.height = this.sizes.THREE_CHARTS.height;
                    containers[i + 2].querySelector("canvas").dataset.width = this.sizes.THREE_CHARTS.width;
                    containers[i + 2].querySelector("canvas").dataset.height = this.sizes.THREE_CHARTS.height;

                    i += 2;
                } else if (size === 'small') {
                    chart.dataset.width = this.sizes.SINGLE_SMALL.width;
                    chart.dataset.height = this.sizes.SINGLE_SMALL.height;
                } else if (size === 'medium' && containers[i + 1] && 
                          containers[i + 1].dataset.size === 'medium' && 
                          containers[i + 1].querySelector("canvas") !== null) {
                    //If this chart and the next is medium
                    chart.dataset.width = this.sizes.TWO_CHARTS.width;
                    chart.dataset.height = this.sizes.TWO_CHARTS.height;

                    //And set the next two dataset values
                    containers[i + 1].querySelector("canvas").dataset.width = this.sizes.TWO_CHARTS.width;
                    containers[i + 1].querySelector("canvas").dataset.height = this.sizes.TWO_CHARTS.height;

                    i += 1;
                } else {
                    //By default, set it to the full width
                    chart.dataset.width = this.sizes.FULL_WIDTH.width;
                    chart.dataset.height = this.sizes.FULL_WIDTH.height;
                }
            }

            //Add the necessary modifications to canvas2svg library to work with chartjs
            Context.prototype.getContext = function(contextId) {
                if (contextId === "2d" || contextId === "2D") {
                    return this;
                }
                return null;
            };
            Context.prototype.style = function() { return this.__canvas.style; };
            Context.prototype.getAttribute = function(name) { return this[name]; };
            Context.prototype.addEventListener = function(type, listener, eventListenerOptions) {};
        },
        getSVGs() {
            //Get all
            const containers = document.querySelectorAll('.size-container');
            const svgs = [];
            //Loop through each chart and convert it to an svg
            containers.forEach(c => {
                const chart = c.querySelector("canvas");

                if (!chart && c.querySelector('.table-template') === null &&
                    c.querySelector('.multi-dimension-table-template') === null &&
                    (c.querySelector('.scorecard-template') !== null ||
                    c.querySelector('.multi-metric-scorecard-template') !== null ||
                    c.querySelector('.information-card-template') !== null)) return;//If it is a scorecard/information_card etc (tables handled below)

                let subtitle = '';
                let title = '';
                
                if (c.querySelector('.chart-title') !== null) {
                    title = c.querySelector('.chart-title').innerText;
                }
                // if (c.querySelector('.chart-subtitle') !== null) {
                //     subtitle = c.querySelector('.chart-subtitle').innerText;
                // }
                
                let data = {};
                let svg = null;
                const id = c.id;

                //If it is a table
                if (c.querySelector('.table-template')) {
                    const table = c.querySelector('.table-template');
                    const rows = table.getElementsByTagName('tr');
                    const tableArray = [];

                    for (let i = 0; i < rows.length; i++) {
                        const cells = rows[i].querySelectorAll('td, th .flex-row');
                        const rowArray = [];
                        
                        for (let j = 0; j < cells.length; j++) {
                            let cellText = '';
                            const childNodes = cells[j].childNodes;
                            
                                // Iterate through childNodes to accumulate text from text nodes only
                            childNodes.forEach(node => {
                                if (node.nodeType === Node.TEXT_NODE) {
                                    cellText += node.textContent.trim();
                                }
                            });
                            
                            rowArray.push(cellText);
                        }
                        
                        tableArray.push(rowArray);
                    }
                    data = tableArray;
                } else if (c.querySelector('.multi-dimension-table-template')) {
                    const table = c.querySelector('.multi-dimension-table-template').querySelector('table');
                    const tableArray = [];
                    if (table) {
                        const rows = table.getElementsByTagName('tr');
                        for (let i = 0; i < rows.length; i++) {
                            const cells = rows[i].querySelectorAll('td, th');
                            const rowArray = [];
                            for (let j = 0; j < cells.length; j++) {
                                rowArray.push(cells[j].textContent.trim());
                            }
                            tableArray.push(rowArray);
                        }
                    }
                    data = tableArray;
                } else {
                    //If it is a chart (skip when canvas is missing – e.g. not yet rendered or non-chart widget)
                    if (!chart) return;
                    svg = this.convertToSVG(chart);
                    svg = this.updateTextElements(svg);
                    
                    if (c.querySelector('.line-chart-template') || c.querySelector('.bar-line-chart-template')) {
                        svg = this.addLinesToChart(svg);
                    }

                    const chartjs = Chart.getChart(chart);
                    if (chartjs && chartjs.config) {
                        // Use filtered data that only includes visible datasets
                        data = this.getVisibleChartData(chartjs);
                    } else {
                        // If chart instance doesn't exist, try to get data from stored config
                        if (chart.__chartConfigExport && chart.__chartConfigExport.data) {
                            data = chart.__chartConfigExport.data;
                        }
                    }
                }

                svgs.push({
                    id: id,
                    title: title,
                    subtitle: subtitle,
                    svg: svg,
                    data: data
                });
            });

            return svgs;
        }
    }
});
</script>
